import { useQuery } from "@tanstack/react-query";

import { ApiError, resolveApiUrl } from "@/api/client";

/**
 * `GET /api/readyz` — the deployment-readiness report
 * (`src/flowstack/server/routes/readyz.py`, contract C0.3).
 *
 * The SPA reads it for exactly one fact: **can this deployment actually
 * dispatch compute?** The report's `vcm_config` check is the only place the
 * running server states its execution mode on the wire — `/api/version`
 * carries the platform version and API contract id, nothing about compute,
 * and no other route exposes `FLOWSTACK_VCM_MODE`. So this is the existing
 * endpoint the submit surface asks, rather than a new one.
 *
 * Two properties make it usable from the browser: it is **unauthenticated**
 * (deploy infra cannot log in) and it sends `Cache-Control: no-store`, so a
 * proxy can never hand the page a stale verdict.
 */

/** C0.3's fixed per-check status vocabulary. */
export type ReadyzCheckStatus = "ok" | "failed" | "skipped";

export interface ReadyzCheck {
  status: ReadyzCheckStatus;
  /** One of the fixed reason strings ("unconfigured" / "timeout" /
   *  "unreachable"), or null. Never exception text — the endpoint is
   *  unauthenticated. */
  detail: string | null;
}

export interface ReadyzReport {
  status: "ready" | "not_ready";
  checks: Record<string, ReadyzCheck>;
}

/**
 * What the deployment can do with a submission, as far as `/api/readyz`
 * is willing to say.
 *
 * - `atlas` — `vcm_config: ok`. The atlas config the dispatcher needs is
 *   present; submissions dispatch to real compute.
 * - `stub` — `vcm_config: skipped`. `FLOWSTACK_VCM_MODE` is `stub`, the
 *   dev/CI default. This is a WORKING execution path with a specific
 *   ending, not a broken one: the run dispatches against the in-process
 *   `StubVCMClient`, which — with no `tile_body` — emulates a noop whose
 *   declared outputs are absent, so the run lands `failed_contract`
 *   (`dispatch/vcm_client.py:195`).
 * - `unusable` — `vcm_config: failed`. Something about the compute config
 *   does not hold. Deliberately ONE bucket: the check renders `failed`
 *   for atlas-with-incomplete-config, for an unrecognised mode, AND for
 *   stub mode on a deployment that declared `vcm_config` mandatory via
 *   `$FLOWSTACK_READYZ_REQUIRED` (C0.3 fails closed). Those have different
 *   mechanisms — no dispatcher at all vs. the stub — so the one thing we
 *   may honestly assert across them is that no run submitted here computes
 *   a result. Splitting the bucket would mean guessing which case we are
 *   in, which is the inference this whole surface exists to avoid.
 * - `unknown` — the report did not load, or carried no `vcm_config` entry
 *   (an older server). We say we do not know; we never fill it in.
 */
export type ExecutionMode = "atlas" | "stub" | "unusable" | "unknown";

/** The readiness-report field the execution mode is read from. Exported so
 *  the UI can name its own source rather than hard-coding the string twice. */
export const VCM_CONFIG_CHECK = "vcm_config";

export const READYZ_PATH = "/api/readyz";

export const READYZ_QUERY_KEY = ["readyz"] as const;

/**
 * Narrow an unknown JSON body to a {@link ReadyzReport}, defensively.
 *
 * Same posture as the submit page's manifest handling (Rupert PR-300 r0): an
 * entry that is not shaped like a check is DROPPED rather than rendered as if
 * it were one. A malformed report degrades to `unknown`, never to a claim.
 */
export function _narrowReadyzReport(body: unknown): ReadyzReport | null {
  if (typeof body !== "object" || body === null) return null;
  const raw = body as { status?: unknown; checks?: unknown };
  if (raw.status !== "ready" && raw.status !== "not_ready") return null;
  if (typeof raw.checks !== "object" || raw.checks === null || Array.isArray(raw.checks)) {
    return null;
  }
  const checks: Record<string, ReadyzCheck> = {};
  for (const [name, value] of Object.entries(raw.checks as Record<string, unknown>)) {
    if (typeof value !== "object" || value === null) continue;
    const check = value as { status?: unknown; detail?: unknown };
    if (check.status !== "ok" && check.status !== "failed" && check.status !== "skipped") continue;
    checks[name] = {
      status: check.status,
      detail: typeof check.detail === "string" ? check.detail : null,
    };
  }
  return { status: raw.status, checks };
}

/** The execution mode a report implies. `null`/absent check → `unknown`. */
export function executionModeFromReport(report: ReadyzReport | null | undefined): ExecutionMode {
  const check = report?.checks?.[VCM_CONFIG_CHECK];
  if (!check) return "unknown";
  switch (check.status) {
    case "ok":
      return "atlas";
    case "skipped":
      return "stub";
    case "failed":
      return "unusable";
  }
}

/**
 * Fetch the readiness report.
 *
 * Not `apiFetch`: readyz answers **503 with the full report body** when a
 * mandatory check fails, and `apiFetch` turns any non-2xx into an `ApiError`
 * built by `_normalizeErrorBody` — which would discard `checks` and leave us
 * with `unknown` on precisely the deployments whose mode matters most. 200 and
 * 503 are both report-bearing here; anything else is not a report.
 */
async function fetchReadyz(): Promise<ReadyzReport> {
  const url = await resolveApiUrl(READYZ_PATH);
  const response = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (response.status !== 200 && response.status !== 503) {
    // An `ApiError` rather than a bare `Error` so the app's one retry policy
    // (`@/api/retry`) classifies it: a 404 from a server without this route is
    // an answer and is not retried; a 502 from a cold proxy may differ next
    // time and is.
    throw new ApiError(response.status, {
      error_code: "readyz_unavailable",
      message: `GET ${READYZ_PATH} returned ${response.status}`,
    });
  }
  const report = _narrowReadyzReport((await response.json()) as unknown);
  if (report === null) {
    throw new Error(`GET ${READYZ_PATH} returned a body that is not a readiness report`);
  }
  return report;
}

/**
 * The deployment's execution mode, for surfaces that must not imply a
 * capability the deployment does not have.
 *
 * `staleTime` is generous because the answer is a property of the deployment's
 * environment, not of anything a user does: it changes on a redeploy, long
 * after the page that read it was mounted. Retries are left to the app's one
 * policy (`@/api/retry`) rather than pinned here — this endpoint is
 * unauthenticated and bounds its own backend cost with a server-side TTL, but
 * there is no reason for it to be the one query with its own rules.
 */
export function useExecutionMode() {
  const query = useQuery({
    queryKey: READYZ_QUERY_KEY,
    queryFn: fetchReadyz,
    staleTime: 5 * 60_000,
  });
  return {
    ...query,
    mode: query.isSuccess ? executionModeFromReport(query.data) : ("unknown" as ExecutionMode),
  };
}
